import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Badge from "../badge/Badge";
import Title from "../title/Title";
import SearchInput from "../searchInput/SearchInput";
import { SecondaryButton } from "../buttonComponent/ButtonComponent";
import { normalizeDispatchOccupancy } from "../../config/utilities/dispatchOccupancyHelpers";
import ModalCloseButton from "../modal/ModalCloseButton";
import BusIcon from "../map/img/bus.svg";
import "./DispatchOccupancyCell.css";
import "../modal/DeleteModal.css";

const OCCUPANCY_BADGE_PROPS = {
  background: "#F2F4F7",
  color: "#344054",
};

const OCCUPANCY_TOOLTIP_STUDENT_LIMIT = 10;

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const getOnboardChildPrimaryName = (child) =>
  String(child?.name ?? "").trim() || "Unknown student";

const getOnboardChildPreferredLabel = (child) => {
  const preferred = child?.preferredName?.trim();
  const name = getOnboardChildPrimaryName(child);

  if (!preferred || preferred.toLowerCase() === name.toLowerCase()) {
    return null;
  }

  return `Preferred Name: ${preferred}`;
};

export const formatOnboardChildName = (child) => {
  const name = getOnboardChildPrimaryName(child);
  const preferred = child?.preferredName?.trim();

  if (preferred && preferred.toLowerCase() !== name.toLowerCase()) {
    return `${name} ${preferred}`;
  }

  return name;
};

const buildDispatchOccupancyTooltipHtml = (childrenOnboard = []) => {
  if (!childrenOnboard.length) return null;

  const sortedChildren = [...childrenOnboard].sort((a, b) =>
    formatOnboardChildName(a).localeCompare(formatOnboardChildName(b)),
  );
  const visibleChildren = sortedChildren.slice(0, OCCUPANCY_TOOLTIP_STUDENT_LIMIT);
  const hasMoreStudents = sortedChildren.length > OCCUPANCY_TOOLTIP_STUDENT_LIMIT;

  const listItems = visibleChildren
    .map((child) => {
      const name = escapeHtml(getOnboardChildPrimaryName(child));
      const preferredLabel = getOnboardChildPreferredLabel(child);

      if (preferredLabel) {
        return `<li>${name}<br /><span style="color:#667085;font-size:0.6875rem;">${escapeHtml(preferredLabel)}</span></li>`;
      }

      return `<li>${name}</li>`;
    })
    .join("");

  const footer = hasMoreStudents
    ? `<div class="app-tooltip-footer">Click the row to see more.</div>`
    : "";

  return `
    <div class="app-tooltip-title">Students on board</div>
    <ol>${listItems}</ol>
    ${footer}
  `.trim();
};

const buildDisplayRows = (children) =>
  [...children]
    .sort((a, b) => formatOnboardChildName(a).localeCompare(formatOnboardChildName(b)))
    .map((child, index) => ({
      key: child.childId || `${getOnboardChildPrimaryName(child)}-${index}`,
      primaryName: getOnboardChildPrimaryName(child),
      preferredLabel: getOnboardChildPreferredLabel(child),
      searchText: formatOnboardChildName(child).toLowerCase(),
    }));

function DispatchOccupancyModalComponent({
  isOpen,
  onClose,
  childrenOnboard = [],
  occupancyLabel,
  tripName,
  vehicleLabel,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  const displayRows = useMemo(
    () => buildDisplayRows(childrenOnboard),
    [childrenOnboard],
  );

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return displayRows;
    return displayRows.filter((row) => row.searchText.includes(term));
  }, [displayRows, searchTerm]);

  if (!isOpen) return null;

  const studentCount = childrenOnboard.length;
  const studentCountLabel = `${studentCount} student${studentCount === 1 ? "" : "s"}`;
  const showSearch = displayRows.length >= 6;

  return createPortal(
    <div className="modal-backdrop dispatch-occupancy-modal-backdrop">
      <div className="dispatch-occupancy-modal" role="dialog" aria-modal="true">
        <div className="user-modal-header dispatch-occupancy-modal-header">
          <div className="user-modal-header-contents">
            <div className="dispatch-occupancy-modal-header-row">
              <div className="dispatch-occupancy-modal-header-leading">
                <div className="icon-container dispatch-occupancy-modal-icon">
                  <img
                    style={{ margin: "auto" }}
                    src={BusIcon}
                    alt=""
                    loading="lazy"
                    aria-hidden="true"
                  />
                </div>
                <div className="user-modal-header-title">
                  <Title
                    title="Students on board"
                    subtitle={`${studentCountLabel} · ${occupancyLabel} capacity`}
                    titleSize="1.125rem"
                    subtitleSize="0.875rem"
                    gap="0.25rem"
                    lineHeight="1.75rem"
                  />
                </div>
              </div>
              <ModalCloseButton
                onClick={onClose}
                className="user-x-button dispatch-occupancy-modal-close"
              />
            </div>
          </div>
        </div>

        {(vehicleLabel || tripName) && (
          <div className="dispatch-occupancy-modal-meta">
            {vehicleLabel ? (
              <div className="dispatch-occupancy-modal-meta-item">
                <span className="dispatch-occupancy-modal-meta-label">Vehicle</span>
                <span className="dispatch-occupancy-modal-meta-value" title={vehicleLabel}>
                  {vehicleLabel}
                </span>
              </div>
            ) : null}
            {vehicleLabel && tripName ? (
              <span className="dispatch-occupancy-modal-meta-divider" aria-hidden="true" />
            ) : null}
            {tripName ? (
              <div className="dispatch-occupancy-modal-meta-item">
                <span className="dispatch-occupancy-modal-meta-label">Trip</span>
                <span className="dispatch-occupancy-modal-meta-value" title={tripName}>
                  {tripName}
                </span>
              </div>
            ) : null}
          </div>
        )}

        <div className="dispatch-occupancy-modal-body">
          {showSearch ? (
            <SearchInput
              containerWidth="100%"
              text="Search students..."
              value={searchTerm}
              onChangeText={(e) => setSearchTerm(e.target.value)}
            />
          ) : null}

          <div className="dispatch-occupancy-modal-list-wrap">
            {filteredRows.length === 0 ? (
              <p className="dispatch-occupancy-modal-empty">
                {searchTerm.trim()
                  ? "No students match your search."
                  : "No students onboard."}
              </p>
            ) : (
              <ol className="dispatch-occupancy-modal-list">
                {filteredRows.map((row, index) => (
                  <li key={row.key} className="dispatch-occupancy-modal-list-item">
                    <span className="dispatch-occupancy-modal-list-index">
                      {index + 1}
                    </span>
                    <div className="dispatch-occupancy-modal-list-content">
                      <span className="dispatch-occupancy-modal-list-name">
                        {row.primaryName}
                      </span>
                      {row.preferredLabel ? (
                        <span className="dispatch-occupancy-modal-list-preferred">
                          {row.preferredLabel}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div className="dispatch-occupancy-modal-actions">
          <SecondaryButton label="Close" width="auto" onClickFunction={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export const DispatchOccupancyModal = memo(DispatchOccupancyModalComponent);

export default function DispatchOccupancyCell({ occupancy, tripStatus }) {
  const normalizedOccupancy = useMemo(
    () => normalizeDispatchOccupancy(occupancy, tripStatus),
    [occupancy, tripStatus],
  );
  const occupancyLabel = `${normalizedOccupancy?.currentOccupancy ?? 0}/${normalizedOccupancy?.maxCapacity ?? 0}`;
  const childrenOnboard = normalizedOccupancy?.childrenOnboard ?? [];
  const hasChildren = childrenOnboard.length > 0;
  const occupancyTooltip = useMemo(
    () => (hasChildren ? buildDispatchOccupancyTooltipHtml(childrenOnboard) : undefined),
    [childrenOnboard, hasChildren],
  );

  return (
    <Badge
      width="fit-content"
      text={occupancyLabel}
      {...OCCUPANCY_BADGE_PROPS}
      tooltip={occupancyTooltip}
      showInfoDot={hasChildren}
    />
  );
}
